import {UserStore} from './user-store';
import {SessionStore} from './session-store';
import {RouterStore} from 'mobx-router';
import {RootStore} from '../base/root-store';
import {routes} from '../base/routes';
import {LabelStore} from '../base/label-store';
import {PushNotificationStore} from './push-notification-store';
import {action, makeObservable, observable} from 'mobx';
import {Capacitor} from '@capacitor/core';

export class LoginViewStore {

    @observable
    state: 'loading' | 'error' | 'idle' = 'idle';

    @observable
    stage: 'start' | 'email' | 'phone' | 'otp' = 'start';

    @observable
    invalid: boolean = false;

    @observable
    showValidation: boolean = false;

    @observable
    emailOrPhone: string = '';

    @observable
    errorReason: 'otp-wait-period' | 'generic' | 'employee-not-found' | undefined = 'generic';

    constructor(private sessionStore: SessionStore, private userStore: UserStore, private router: RouterStore<RootStore>, public labelStore: LabelStore, private pushNotificationStore: PushNotificationStore) {
        makeObservable(this);
    }

    @action
    onEnter(): void {
        this.stage = 'start';
        this.state = 'idle';
        this.invalid = false;
        this.showValidation = false;
    }

    @action
    goToEmailLogin(): void {
        this.stage = 'email';
        this.state = 'idle';
        this.invalid = false;
        this.showValidation = false;
    }

    @action
    goToPhoneLogin(): void {
        this.stage = 'phone';
        this.state = 'idle';
        this.invalid = false;
        this.showValidation = false;
    }

    @action
    goToMobileStartPage(): void {
        this.stage = 'start';
        this.state = 'idle';
        this.invalid = false;
        this.showValidation = false;
    }

    @action
    goToOtpPage(): void {
        this.stage = 'otp';
        this.state = 'idle';
        this.invalid = false;
        this.showValidation = false;
    }

    @action
    async adminLogin(employeeEmail: string, adminPassword: string, redirectToDashboard: boolean): Promise<void> {
        await this.load(async () => {
            const success = await this.sessionStore.adminLogin(employeeEmail, adminPassword);

            if (!success) {
                this.invalid = true;

                return;
            }

            await this.userStore.loadUser();

            if (redirectToDashboard) {
                await this.router.goTo(routes.dashboard);
            }
        });
    }

    @action
    async initiateEmailLogin(email: string): Promise<void> {
        await this.load(async () => {
            const isMobileApp = Capacitor.getPlatform() !== 'web';
            const loginResponse = await this.sessionStore.initiateEmailLogin(email, isMobileApp);

            if (!loginResponse.success) {
                this.invalid = true;
                this.errorReason = loginResponse.failureReason;

                return;
            }

            if (loginResponse.tenantId !== undefined) {
                this.invalid = false;

                await this.completeSsoLogin(loginResponse.tenantId);
            } else {
                this.emailOrPhone = email;
                this.stage = 'otp';
                this.invalid = false;
            }
        });
    }

    @action
    async initiatePhoneLogin(phone: string): Promise<void> {
        const correctedPhone = phone.replace(/\s+/g, '');
        const isMobileApp = Capacitor.getPlatform() !== 'web';

        if (!this.checkPhoneNumber(correctedPhone)) {

            await this.load(async () => {
                const loginResponse = await this.sessionStore.initiatePhoneLogin(correctedPhone, isMobileApp);

                if (!loginResponse.success) {
                    this.invalid = true;

                    return;
                }
                if (loginResponse.tenantId !== undefined) {
                    this.invalid = false;
                    await this.completeSsoLogin(loginResponse.tenantId);
                } else {
                    this.emailOrPhone = correctedPhone;
                    this.stage = 'otp';
                    this.invalid = false;
                }
            });
        } else {
            this.showValidation = true;
        }
    }

    @action
    async completeOtpLogin(emailOrPhone: string, otpCode: string): Promise<void> {
        const isMobileApp = Capacitor.getPlatform() !== 'web';

        await this.load(async () => {
            const success = await this.sessionStore.completeOtpLogin(emailOrPhone, otpCode, isMobileApp);

            if (!success) {
                this.invalid = true;
                this.stage = 'otp';

                return;
            }
            await this.loadUser();
        });
    }

    @action
    async completeSsoLogin(tenantId: string): Promise<void> {
        await this.load(async () => {
            const success = await this.sessionStore.createSsoSession(tenantId);

            if (!success) {
                this.invalid = true;
                this.stage = 'start';

                return;
            }
            await this.loadUser();
        });
    }

    @action
    async loadUser(): Promise<void> {

        this.invalid = false;

        await this.userStore.loadUser();

        if (this.pushNotificationStore.isSupported()) {
            await this.pushNotificationStore.registerPushToken();
        }

        if (Capacitor.getPlatform() !== 'web') {
            await this.router.goTo(routes.chat);
        }
    }

    @action
    async load(process: () => void): Promise<void> {
        try {
            this.state = 'loading';
            await process();
            this.state = 'idle';
        } catch (e) {
            this.state = 'error';
            console.warn(e);
        }
    }

    checkPhoneNumber(value: string): undefined | 'invalid-phone-format' {
        const correctedPhoneNumber = value.replace(/\s+/g, '');
        const phoneNumberWithoutPlus = correctedPhoneNumber.substr(1, correctedPhoneNumber.length);

        if (!/^\d+$/.test(phoneNumberWithoutPlus)) {
            return 'invalid-phone-format';
        } else if (correctedPhoneNumber.charAt(0) !== '+') {
            return 'invalid-phone-format';
        } else {
            return undefined;
        }
    }
}
